import React, { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import Image from "next/image"
import TooltipIcon from "@/public/icons/small-tooltip.svg"
import ToolTip from "@/components/core/ToolTip/ToolTip"
import Link from "next/link"
import _get from "lodash/get"
import _isEmpty from "lodash/isEmpty"
import { NumericFormat } from "react-number-format"
import htmlParser from "html-react-parser"

import { selectCartState } from "@/store/features/cartSlice"

import {
  checkStock,
  isPriceZero,
  checkIfImageExists,
  getPDPUrl,
  pdpTypeFormat,
  sanitizeTextForAnalytics,
  getPresetUrl,
} from "@/utils/helper"

import {
  getTmpType,
  getAllAdditinalInfo,
  getDiscountPrice,
  getReturnStatusDescription,
  checkReturnStatus,
  servicesItem,
  getProductImageAlt,
} from "@/components/cart/CartItemTile/utils"
import { getConfig } from "@/constants/config"
import { annSacksConstants, SERVICES } from "@/constants/index"
import {
  addCartItemSummaryAnalytics,
  addRemoveProductFromCartAnalytics,
  addDatalayer,
} from "@/components/cart/cartAnalytics"
import {
  cartBundleFinishAnalytics,
  cartItemSummaryAnalytics,
  getTrackingLinkAnalyticsData,
} from "@/components/cart/CartItemTile/analytics"

import QuantitySelector from "@/components/cart/QuantitySelector"
import Radio from "@/components/core/Radio/Radio"

import {
  removeCartItem,
  getMessageStatus,
  handleQuantityChange,
} from "@/components/cart/cartHelper"
import useWindowResize from "@/hooks/useWindowResize"
import PartsViewer from "@/components/PartsViewer/v1/PartsViewer"
import RecalculateModal from "@/components/cart/RecalculateModal"
import BundlesViewDetails from "@/components/Bundles/StaticBundle/BundlesViewDetails"

import MissingImage from "@/public/images/image-missing.png"
import errorMsg from "@/public/images/error-qty.svg"

import { selectGenericState } from "@/store/features/genericSlice"
import {
  LINE_ITEM_STATUS_RETURN_CANCELLED,
  LINE_ITEM_STATUS_RETURN_REJECTED,
} from "@/components/OrderConfirmation/OrderDetailsTabs/constant"
import { formatDate, parseDate } from "@/utils/dateHelper"
import OrderCancellationDetails from "@/components/cart/OrderCancellationDetails"

import { addAnalyticsBackOrderTooltip } from "@/components/checkout/v2/analytics"

const CartItemTile = props => {
  const {
    data = {},
    checkoutMode,
    i18n = {},
    isDeliveryOption,
    comboChild,
    loading,
    orderMode,
    hideAlerts,
    page,
    is3COrder,
    linkCustomAnalyticsData,
    linkAnalyticsData = ``,
    trackingLinkAnalyticsData = "",
    email = "",
    showRadio,
    isAlert,
    isCartPage,
    isHideItemPrice,
    isModel = false,
    handleRoomInfoUpdate,
    presetConfigs = "",
    activeTab = "",
    includeBackorderToolTip = false,
    backOrderTooltipMessage = {},
    isShippingAlert = false,
    zipCode = "",
    returnDefectInfo = {},
    parcels = [],
    deliveryMethod = {},
  } = props || {}

  const {
    availableQty = 0,
    quantity = 0,
    discountPrice = 0,
    unitPrice = 0,
    totalPrice = 0,
    category = "",
    slug = "",
    sku = "",
    status = "",
    discountedPricePerQuantity = 0,
    discountedPriceProductLevel = 0,
    isBundleParent = false,
    bundleLineItems = [],
    bundleItemsCount = 0,
    id = "",
    isComboSku = "",
    backOrder = false,
    cancellationInfo = [],
    cancelledQuantity,
    isBundleChildItem,
  } = data

  const { returnCancelledMessage = "", returnRejectedMessage = "" } =
    returnDefectInfo

  const {
    availableToShipMsg = () => {},
    outOfStock = "",
    outOfStockText = "",
    locationLabel = "",
    inPersonLabel = "",
  } = i18n

  const { qtyLoading } = useSelector(selectCartState)

  const [width] = useWindowResize()
  const discountVal = isPriceZero(discountedPricePerQuantity)
    ? discountedPriceProductLevel
    : discountedPricePerQuantity
  const [prodQuantity, setProdQuantity] = useState(data.quantity)
  const [alert, setAlert] = useState({ show: false, mssg: "" })
  const [currencySign, setCurrencySign] = useState("")
  const [productAria, setProductAria] = useState("")
  const [partsModal, setPartsModal] = useState(false)
  const siteName = useRef(null)
  const [action, setAction] = useState("remove")
  const [allAdditinalInfo, setAllAdditinalInfo] = useState({})
  const [isModalShow, setIsModalShow] = useState(false)
  const [imageUrl, setImageUrl] = useState(data?.image ?? "")
  const [pdpNavigationURL, setPdpNavigationURL] = useState("")
  const [scene7AccountName, setScene7AccountName] = useState("")
  const [swatchUrl, setSwatchUrl] = useState("")
  const [showTooltip, setShowTooltip] = useState(false)

  const { featureFlags = {} } = useSelector(selectGenericState)
  const { MYSQLCONNSTR_ENABLE_ORDER_DETAILS_TABS: isEnableTabView = false } =
    featureFlags

  const isReturnItem = checkReturnStatus(status)
  const isReturnCancelledOrRejected =
    status === LINE_ITEM_STATUS_RETURN_CANCELLED ||
    status === LINE_ITEM_STATUS_RETURN_REJECTED

  useEffect(() => {
    if (!(qtyLoading || loading)) {
      setProdQuantity(data.quantity)
    }
    getPDPUrl(category, slug).then(ret => {
      let pdpUrl = ret
      if (sku) {
        if (pdpUrl.indexOf("?") > -1) {
          pdpUrl = pdpUrl + "&skuId=" + sku
        } else {
          pdpUrl = pdpUrl + "?skuId=" + sku
        }
      }
      setPdpNavigationURL(pdpUrl)
    })
  }, [data])

  // Check if the item is a service item
  const isServiceItem = servicesItem(data?.category)

  useEffect(() => {
    if (!(qtyLoading || loading)) {
      setProdQuantity(data.quantity)
    }
  }, [qtyLoading, loading])

  useEffect(() => {
    const productAria = `${data.name}. ${data.desc}. ${i18n.colorFinish}: ${
      data.color
    }. ${i18n.qty}: ${quantity}. ${i18n.unitPrice}: ${currencySign}${
      discountPrice > 0 ? discountPrice : unitPrice
    }. ${i18n.total}: ${currencySign}${totalPrice}.`
    setProductAria(productAria)
  }, [data])

  useEffect(() => {
    getConfig().then(config => {
      const currency = _get(config, "internationalization.currencySign", "")
      setCurrencySign(currency)
      siteName.current = _get(config, "general.siteName", "")
      const {
        general: { scene7AccountName = "", swatchUrl = "" },
      } = config
      setScene7AccountName(scene7AccountName)
      setSwatchUrl(swatchUrl)
    })

    const presetConfigsData = presetConfigs ? JSON.parse(presetConfigs) : {}
    checkIfImageExists(data?.image, exists => {
      if (exists) {
        if (includeBackorderToolTip || isShippingAlert) {
          setImageUrl(`${data?.image}?bgc=f9f9f9`)
        } else if (
          !_isEmpty(presetConfigsData) &&
          siteName.current?.toLowerCase() === data?.brand?.toLowerCase()
        ) {
          const presetImageUrl = getPresetUrl(
            width,
            swatchUrl,
            data?.image,
            presetConfigsData,
            scene7AccountName
          )

          checkIfImageExists(presetImageUrl, exists => {
            if (exists) {
              setImageUrl(presetImageUrl)
            }
          })
        }
      } else {
        setImageUrl(MissingImage)
      }
    })
  }, [])

  const handleError = e => {
    e.stopPropagation()
    setImageUrl(MissingImage)
  }

  const handleCallBack = stock => {
    if (stock) {
      const mssg = availableToShipMsg(stock)
      setAlert({ show: true, mssg: mssg })
    } else {
      setAlert({ show: true, mssg: outOfStock })
    }
  }

  const handleChange = qty => {
    setProdQuantity(qty)
    setAlert({ show: false, mssg: "" })
    const payload = {
      id,
      sku,
      quantity: qty,
      isComboSku,
      isBundleParent,
      stockCheck: checkStock(backOrder, category),
      cb: handleCallBack,
      staticText: i18n,
      zipCode,
    }
    handleQuantityChange(payload)
  }

  const {
    additionalData: { roomInfo = [], overage: totalOverageVal = "" } = {},
  } = data

  const roomInfoDetailsTotal = roomInfo.length

  const {
    totalSFNeeded = 0,
    totalArea = "",
    totalUserNeeded = 0,
    totalSFUserNeeded = 0,
    totalNeeded = 0,
    productType = "",
    soldByBox = "",
    soldByUom = "",
  } = roomInfo[0] ? JSON.parse(roomInfo[0]) : {}

  const showMeasurementVal = productType ? productType?.toLowerCase() : ""

  const handleRecalculateModalClose = () => {
    setIsModalShow(!isModalShow)
    addDatalayer({
      title: `close`,
      section: `recalculate your space modal`,
      productInfo: data?.customProductInfo,
    })
  }
  const handleRecalculateModalOpen = e => {
    setAllAdditinalInfo(getAllAdditinalInfo(e))
    setIsModalShow(!isModalShow)
    addDatalayer({
      title: `recalculate footage`,
      section: `cart items`,
      productInfo: data?.customProductInfo,
    })
  }
  const updateRecalculateCarts = () => {
    let customQuantity = 0
    let additionalInfo = {}
    const updateCartAttr =
      document.querySelector(".trim-calculator_main-block") &&
      document.querySelector(".trim-calculator_main-block") !== null
        ? document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("templatetype")
        : ""
    if (updateCartAttr === "trim" || updateCartAttr === "tile") {
      let totalArea = 0
      let totalOverage = Number(
        document.querySelector("#trim-calculator__overage-value-selected")
          ? document.querySelector("#trim-calculator__overage-value-selected")
              ?.innerText
          : ""
      )
      totalOverage =
        document.querySelector("#trim-calculator__overage-input-box")?.value ??
        totalOverage
      let customQuantityTmp = ""
      let soldByBox = ""
      let soldByUom = ""
      let totalNeeded = 0
      if (document.querySelector(".trim-calculator_main-block")) {
        customQuantityTmp = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("totalpeices")
        )
        soldByUom = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("soldbyuom")
        )
        soldByBox = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("soldbybox")
        )
        totalArea = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("totaloverage")
        )
        totalNeeded = Number(
          document.querySelector(".trim-calculator__square-ft-value")
            ? document.querySelector(".trim-calculator__square-ft-value").value
            : ""
        )
        customQuantity =
          customQuantityTmp && customQuantityTmp > 0 ? customQuantityTmp : 0
      }
      if ((totalArea !== "" && totalArea > 0) || totalOverage !== "") {
        additionalInfo = {
          productType: updateCartAttr,
          totalNeeded: totalNeeded,
          totalArea: totalArea.toFixed(2),
          soldByBox: soldByBox,
          soldByUom: soldByUom,
          customQuantity: customQuantity,
          overage: totalOverage.toString(),
          roomInfo: true,
        }
      }
    }
    if (customQuantity > 0 && data.sku) {
      handleRoomInfoUpdate(data.sku, customQuantity, additionalInfo)
    }
    setIsModalShow(!isModalShow)
  }
  const discountPriceProduct = Number(data.discountPrice)
  const customerFacingSKUId = data.customerFacingSKU || data.sku

  const cartItemProductInfo =
    linkCustomAnalyticsData ??
    data.customProductInfo ??
    JSON.stringify({
      productInfo: {
        productBrand: siteName.current?.toLowerCase(),
        description: sanitizeTextForAnalytics(data.desc),
        globalPromotionPrice: discountPriceProduct
          ? discountPriceProduct
          : Number(data.unitPrice),
        priceState:
          data.discountPercent > 0
            ? "percentOff|" + data.discountPercent
            : "regular price",
        productBasePrice: Number(data.unitPrice),
        productCategory: sanitizeTextForAnalytics(data.category),
        productColor: sanitizeTextForAnalytics(data.color) || "n/a",
        productID: customerFacingSKUId,
        productName: sanitizeTextForAnalytics(data.name),
        productRoom: sanitizeTextForAnalytics(data.category),
        productSalePrice: discountPriceProduct
          ? discountPriceProduct
          : Number(data.unitPrice),
        productStatus:
          data.outOfStock && data.availableQty < 1
            ? "out of stock"
            : "in stock",
        productSuperSku: data.superSku ? true : false,
        productTileName: sanitizeTextForAnalytics(data.name),
        quantity: data.quantity,
        defaultImageName: data.image,
        productCollectionsName: sanitizeTextForAnalytics(
          data.productCollectionsName || data.category
        ),
        productCoupondiscount: "n/a",
        productPartnerBuyNow: "true",
        productSaleable: "true",
        isRecommended: "true",
        isSendNow: "true",
        isSubscription: "true",
        frequency: "n/a",
        ratings: "n/a",
        numberOfReviews: "n/a",
        pdpType:
          isBundleParent || data?.isBundleChildItem
            ? "bundle"
            : pdpTypeFormat({
                businessUnitName: data.businessUnitName,
                ProductIsRetail: data.ProductIsRetail,
                discontinuedProductb: data.discontinuedProductb,
                ProductRequiredAccessoriesGroupsList:
                  data.ProductRequiredAccessoriesGroupsList,
                superSku: data.superSku,
              }),
        productFindingMethod: "n/a",
      },
    })

  const isMsgStatus = getMessageStatus({
    data,
    email,
    hideAlerts,
    i18n,
    checkoutMode,
  })
  const isMsgMobileStatus = getMessageStatus({
    data,
    email,
    hideAlerts,
    i18n,
    isMobile: true,
    alert,
  })

  const handleRemoveItem = () => {
    const payloadData = {
      id,
      quantity,
      isBundle: isComboSku,
      isBundleParent,
      sku,
      isComboSku,
    }

    const bundleLocalStorage = localStorage?.getItem("bundleItems")
    if (bundleLocalStorage) {
      cartBundleFinishAnalytics(bundleLocalStorage, payloadData)
    }

    removeCartItem(payloadData)
    addRemoveProductFromCartAnalytics(
      "cmp:click",
      isBundleParent || data?.isBundleChildItem,
      cartItemProductInfo ? JSON.parse(cartItemProductInfo) : ""
    )
  }

  const handleProductClickAnalytics = () => {
    const eventInfo = addCartItemSummaryAnalytics(
      page,
      data.desc,
      data.name,
      pdpNavigationURL,
      isBundleParent || data?.isBundleChildItem,
      isEnableTabView,
      activeTab
    )
    cartItemSummaryAnalytics(eventInfo, cartItemProductInfo)
  }

  const isAsProduct =
    data?.sourceBrand?.toLowerCase() === annSacksConstants?.asSourceChannel

  const shouldRenderQuantity = isEnableTabView ? !isComboSku : !comboChild

  const shouldRenderTrackingButtons = (
    trackingLinks = [],
    svgId = "",
    isTabsEnabled
  ) => {
    if (isTabsEnabled) {
      return trackingLinks.length > 0
    } else {
      return !!(trackingLinks || svgId)
    }
  }

  let shouldRenderPrice

  if (isEnableTabView) {
    shouldRenderPrice = orderMode ? comboChild || !isComboSku : !comboChild
  } else {
    shouldRenderPrice = !comboChild
  }

  const backOrderTooltip = () => {
    return (
      <span>
        <div className="cart-item__tooltip">
          <img
            src={TooltipIcon.src}
            alt=""
            onMouseOver={e => handleInfoToolTipHover(e)}
            onMouseLeave={() => setShowTooltip(false)}
            width={"16px"}
            height={"16px"}
          />
          {showTooltip && <ToolTip message={backOrderTooltipMessage?.value} />}
        </div>
      </span>
    )
  }

  const handleInfoToolTipHover = e => {
    e.stopPropagation()
    setShowTooltip(true)
    if (includeBackorderToolTip) {
      addAnalyticsBackOrderTooltip({
        title: i18n?.backOrderMssg(data?.leadTime).toLowerCase(),
        parcels,
        deliveryMethod,
        tooltipMsg: backOrderTooltipMessage?.value.toLowerCase(),
      })
    }
  }

  const isService = SERVICES.includes(category)
  const shapeSizeDesignArray = []

  if (data?.shape) shapeSizeDesignArray.push(data?.shape)
  if (data?.size) shapeSizeDesignArray.push(data?.size)

  return (
    <>
      {isModalShow && (
        <RecalculateModal
          allAdditinalInfo={allAdditinalInfo}
          updateRecalculateCarts={updateRecalculateCarts}
          onClose={handleRecalculateModalClose}
          isShow={isModalShow}
          i18n={i18n}
          productInfo={data?.customProductInfo}
          sku={sku}
          stockValue={quantity}
        />
      )}
      {comboChild && (
        <div className="cart-item__item-of">
          {/* Hide 'item of' text when tabs are enabled */}
          {!isEnableTabView
            ? i18n.itemOf(comboChild.index, comboChild.count)
            : ""}
        </div>
      )}
      <div className="row cart-item no-gutters">
        {partsModal && (
          <PartsViewer
            page="cart"
            i18n={i18n}
            productId={data.svgProductId}
            showModal={partsModal}
            partId={data.svgId}
            onClose={() => setPartsModal(false)}
            analyticsCustomData={cartItemProductInfo}
          />
        )}
        <div
          className={`col-12 ${
            !checkoutMode ? "col-sm-6" : "col-sm-12"
          } mobile-full-width ${
            checkoutMode
              ? "col-sm-12"
              : isDeliveryOption || isAlert || orderMode
              ? "col-md-8"
              : "col-md-6"
          } print-left ${
            comboChild && orderMode
              ? `cart-item__combo-item col-md-6`
              : comboChild
              ? "cart-item__combo-item col-md-12"
              : ""
          }`}
        >
          <div className="cart-item__summary">
            <Link
              className="gbh-data-layer"
              tabIndex={0}
              href={pdpNavigationURL}
              onClick={handleProductClickAnalytics}
            >
              <Image
                className="cart-item__image"
                src={imageUrl}
                alt={getProductImageAlt(data)}
                width={300}
                height={300}
                onError={handleError}
              />
            </Link>
            <section
              className={`cart-item__specs ${
                isEnableTabView ? "cart-item__specs-tabs" : ""
              }`}
            >
              <div className="row no-gutters">
                <div className="col-12 col-sm-6 col-md-7 cart-item-desc__name">
                  <div className="cart-item__brand">
                    {data.brand && (
                      <span className="cart-item__brand-name">
                        {data.brand}
                      </span>
                    )}
                  </div>

                  {comboChild && isDeliveryOption && data.name ? (
                    <span className="cart-item__desc">{data.desc}</span>
                  ) : null}

                  {!comboChild && (
                    <a
                      href={pdpNavigationURL}
                      role="button"
                      className="cart-item__name-desc"
                      tabIndex={-1}
                      aria-description={productAria}
                      aria-hidden="true"
                      onClick={handleProductClickAnalytics}
                    >
                      <span className="cart-item__name">
                        {data.name || data.desc}{" "}
                      </span>

                      {data.name && data.name !== data.desc && (
                        <span className="cart-item__desc">{data.desc}</span>
                      )}
                    </a>
                  )}
                  {checkoutMode && zipCode ? (
                    <p className="location">
                      {locationLabel}: {zipCode} ({inPersonLabel})
                    </p>
                  ) : null}
                  {data?.additionalData && roomInfoDetailsTotal > 0 && (
                    <div className="cart-landing-recalculate__recalculate-main-block">
                      <div className="cart-landing-recalculate__recalculate-main-block-second">
                        {i18n.coverage}

                        <span className="cart-landing-recalculate__overage-label-text">
                          {totalArea}{" "}
                          {showMeasurementVal === "trim" ? "LF" : "SF"}
                        </span>

                        {!checkoutMode && (
                          <span>
                            <a
                              href="#"
                              className="cart-landing-recalculate__popup-link"
                              onClick={e => handleRecalculateModalOpen(e)}
                              data-totalneeded={totalNeeded}
                              data-name={data.name ? data.name : data.desc}
                              data-desc={data.desc}
                              data-image={data.image}
                              data-currencysign={currencySign}
                              data-price={data.unitPrice}
                              data-totalarea={totalArea}
                              data-totaloverageval={totalOverageVal}
                              data-soldbybox={soldByBox}
                              data-soldbyuom={soldByUom}
                              data-templatetype={showMeasurementVal}
                              data-discountprice={data.discountPrice}
                              data-totalprice={data.totalPrice}
                              data-totalSFNeeded={totalSFNeeded}
                              data-totalUserNeeded={totalUserNeeded}
                              data-totalSFUserNeeded={totalSFUserNeeded}
                              data-totalBoxes={data?.quantity}
                            >
                              {"Recalculate"}
                            </a>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {!isService && data.color !== "Not Applicable" && (
                    <>
                      {!isBundleParent ? (
                        <p className="cart-item__color">
                          {i18n.colorFinish}: {data.color}
                        </p>
                      ) : null}
                      {shapeSizeDesignArray.length > 0 ? (
                        <p className="cart-item__shape">
                          {`${i18n?.shape} 
                            ${shapeSizeDesignArray.join(", ")}`}
                        </p>
                      ) : null}
                    </>
                  )}
                  <p className="cart-item__sku">
                    {i18n.sku} : {data.customerFacingSKU || data.sku}
                  </p>
                  {includeBackorderToolTip &&
                    availableQty < 1 &&
                    !backOrder &&
                    !isServiceItem && (
                      <p className="cart-item__outofstock">{outOfStockText}</p>
                    )}
                  {!comboChild && status === "Cancelled" ? (
                    <div className="cart-item__cancellation-details">
                      <OrderCancellationDetails
                        i18n={i18n}
                        cancellationInfo={cancellationInfo}
                        cancelledQuantity={cancelledQuantity}
                        availableQty={availableQty}
                        quantity={quantity}
                      />
                    </div>
                  ) : null}
                  {isModel ? (
                    <p className="cart-item__qty_mobile">
                      {i18n.qty} : {quantity}
                    </p>
                  ) : null}
                  {!checkoutMode && isBundleParent ? (
                    <BundlesViewDetails
                      lineItem={bundleLineItems}
                      bundleItemsCount={bundleItemsCount}
                      productInfoData={cartItemProductInfo}
                      page={page}
                    />
                  ) : null}
                  {!checkoutMode &&
                    data.isComboSku &&
                    !isDeliveryOption &&
                    !orderMode && (
                      <p className="cart-item__note --mobile">
                        {i18n.note}: {i18n.comboSkuMssg}
                      </p>
                    )}
                  {email && (
                    <p className="d-block d-lg-none cart-item__email">
                      {i18n.email}: {email}
                    </p>
                  )}
                  {email &&
                    (data.category === "Install Services" ||
                      data.category === "Design Services") && (
                      <p className="cart-item__note --e-delivery">
                        {i18n.note}:{" "}
                        {data.category === "Install Services"
                          ? i18n.eDeliveryMsg
                          : i18n.eDeliveryNote}
                      </p>
                    )}
                  {!data.cancelled &&
                    hideAlerts &&
                    data.backOrder &&
                    data.leadTime &&
                    includeBackorderToolTip &&
                    data.quantity > data.availableQty &&
                    i18n.backOrderMssg && (
                      <>
                        <p className="cart-item__stock-msg checkout-backOrder">
                          {i18n.backOrderMssg(data.leadTime)}
                          {includeBackorderToolTip && backOrderTooltip()}
                        </p>
                      </>
                    )}
                  {isDeliveryOption ? (
                    <p className="cart-item__quantity-price">
                      <span className="cart-item__label --count">
                        <span>{i18n.qtyLabel} : </span>
                        {`${data.quantity}`}
                      </span>
                      <span className="cart-item__label --sum">
                        <NumericFormat
                          value={data.totalPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={currencySign}
                          decimalScale={2}
                          fixedDecimalScale={true}
                        />
                      </span>
                    </p>
                  ) : null}
                  {!isDeliveryOption &&
                    !isCartPage &&
                    data.discountPrice > 0 && (
                      <div className="cart-item__display-discount --mobile">
                        <div className="cart-item__discount-price --offPercentage ">
                          <span>
                            {data.discountPercent
                              ? `${data.discountPercent}${i18n.discountPercentage}`
                              : data.discountAmount
                              ? `${currencySign}${data.discountAmount} ${i18n.off}`
                              : ""}
                          </span>
                        </div>
                        <div className="cart-item__discount-price --mobile">
                          <NumericFormat
                            value={data.unitPrice * data.quantity}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={currencySign}
                            decimalScale={2}
                            fixedDecimalScale={true}
                          />
                        </div>
                      </div>
                    )}
                  {(checkoutMode || orderMode) &&
                  comboChild &&
                  !isDeliveryOption ? (
                    <>
                      {data.name && (
                        <span className="cart-item__desc">{data.desc}</span>
                      )}
                      {status === "Cancelled" ? (
                        <div className="cart-item__cancellation-details">
                          <OrderCancellationDetails
                            i18n={i18n}
                            cancellationInfo={cancellationInfo}
                            cancelledQuantity={cancelledQuantity}
                            availableQty={availableQty}
                            quantity={quantity}
                          />
                        </div>
                      ) : null}

                      {/* Hide combo-childs quantity when tabs are enabled */}
                    </>
                  ) : null}
                  {/* '!isEnableTabView' is to hide item price and discount price in tabbed ui */}
                  {!isEnableTabView && orderMode && !isHideItemPrice ? (
                    <p className="order-complete-item-price">
                      {i18n.itemPrice}:{" "}
                      <NumericFormat
                        value={
                          Number(data.discountPrice)
                            ? data.discountPrice
                            : data.unitPrice
                        }
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={currencySign}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                      {Number(data.discountPrice) ? (
                        <NumericFormat
                          className="cart-item__span"
                          value={data.unitPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={currencySign}
                          decimalScale={2}
                        />
                      ) : null}
                    </p>
                  ) : null}
                  <div className="message-container error">
                    <div className="cart-stock-msg desktop-cart-stock-msg">
                      {/* hide cancelled text from tab view */}
                      {!isEnableTabView && data.cancelled && (
                        <div className="row">
                          <div className="col text-right cart-item__stock-msg">
                            {i18n.cancelled}
                          </div>
                        </div>
                      )}

                      {email && (
                        <div
                          className={`${orderMode ? "order-mode-hide" : "row"}`}
                        >
                          <span className="cart-item__stock-msg">
                            {i18n.email} : {email}
                          </span>
                        </div>
                      )}
                      {!hideAlerts && (
                        <>
                          {alert.show && (
                            <div className="cart-item__stock-msg --error avaiable-qty-error">
                              <span className="qty-error-icon">
                                <img src={errorMsg?.src} alt={alert.mssg} />
                              </span>
                              <span>{alert.mssg}</span>
                            </div>
                          )}

                          {!alert.show &&
                            data.outOfStock &&
                            data.availableQty < 1 && (
                              <div className="text-right cart-item__stock-msg --error">
                                <span>{i18n.outOfStock}</span>
                              </div>
                            )}

                          {!alert.show &&
                            data.outOfStock &&
                            data.availableQty > 0 && (
                              <div className="cart-item__stock-msg --error avaiable-qty-error">
                                <span className="qty-error-icon">
                                  <img
                                    src={errorMsg?.src}
                                    alt={availableToShipMsg(data.availableQty)}
                                  />
                                </span>
                                <span>
                                  {availableToShipMsg(data.availableQty)}
                                </span>
                              </div>
                            )}
                          {!alert.show &&
                            !data.outOfStock &&
                            data.noShipping &&
                            !checkoutMode && (
                              <div className="cart-item__stock-msg --error">
                                <span className="error-icon">
                                  <img src={errorMsg?.src} alt={alert.mssg} />
                                </span>
                                <span>{i18n.cannotShip}</span>
                              </div>
                            )}
                        </>
                      )}
                      {!hideAlerts &&
                        data.category !== "Install Services" &&
                        data.category !== "Design Services" && (
                          <>
                            {data.backOrder &&
                            data.leadTime &&
                            data.quantity > data.availableQty ? (
                              <p className="cart-item__stock-msg">
                                {i18n.backOrderMssg(data.leadTime)}
                              </p>
                            ) : (
                              !alert.show && (
                                <p className="cart-item__stock-msg">
                                  {data.outOfStock || data.noShipping
                                    ? ""
                                    : i18n.inStock}
                                </p>
                              )
                            )}
                          </>
                        )}
                      {!data.cancelled &&
                        hideAlerts &&
                        data.backOrder &&
                        data.leadTime &&
                        !includeBackorderToolTip &&
                        data.quantity > data.availableQty &&
                        i18n.backOrderMssg && (
                          <>
                            <p className="cart-item__stock-msg checkout-backOrder">
                              {i18n.backOrderMssg(data.leadTime)}
                              {includeBackorderToolTip && backOrderTooltip()}
                            </p>
                          </>
                        )}
                    </div>
                    {isMsgMobileStatus ? (
                      <div className="cart-stock-message-mobile">
                        <div className="cart-item__qty-mobile">&nbsp;</div>
                        {!isEnableTabView && data.cancelled && (
                          <div className="row">
                            <div className="col text-right cart-item__stock-msg">
                              {i18n.cancelled}
                            </div>
                          </div>
                        )}
                        {email && (
                          <div className="row">
                            <span className="col d-none d-lg-block text-right cart-item__stock-msg">
                              {i18n.email} : {email}
                            </span>
                          </div>
                        )}
                        {!hideAlerts && (
                          <>
                            {alert.show && (
                              <div className="col cart-item__stock-msg --error avaiable-qty-error">
                                <span className="qty-error-icon">
                                  <img src={errorMsg?.src} alt={alert.mssg} />
                                </span>
                                <span>{alert.mssg}</span>
                              </div>
                            )}

                            {!alert.show &&
                              data.outOfStock &&
                              data.availableQty < 1 && (
                                <div className="col text-right cart-item__stock-msg --error">
                                  <span>{i18n.outOfStock}</span>
                                </div>
                              )}

                            {!alert.show &&
                              data.outOfStock &&
                              data.availableQty > 0 && (
                                <div className="col cart-item__stock-msg --error avaiable-qty-error">
                                  <span className="qty-error-icon">
                                    <img
                                      src={errorMsg?.src}
                                      alt={availableToShipMsg(
                                        data.availableQty
                                      )}
                                    />
                                  </span>
                                  <span>
                                    {availableToShipMsg(data.availableQty)}
                                  </span>
                                </div>
                              )}
                          </>
                        )}
                        {!hideAlerts &&
                          data.category !== "Install Services" &&
                          data.category !== "Design Services" && (
                            <>
                              {data.backOrder &&
                              data.leadTime &&
                              data.quantity > data.availableQty ? (
                                <p className="cart-item__stock-msg">
                                  {i18n.backOrderMssg(data.leadTime)}
                                </p>
                              ) : (
                                !alert.show && (
                                  <p className="cart-item__stock-msg">
                                    {data.outOfStock || data.noShipping
                                      ? ""
                                      : i18n.inStock}
                                  </p>
                                )
                              )}
                            </>
                          )}
                        {!data.cancelled &&
                          hideAlerts &&
                          data.backOrder &&
                          data.leadTime &&
                          data.quantity > data.availableQty &&
                          i18n.backOrderMssg && (
                            <>
                              <p className="cart-item__stock-msg">
                                {i18n.backOrderMssg(data.leadTime)}
                              </p>
                            </>
                          )}
                      </div>
                    ) : null}
                    {data.noShipping && isBundleChildItem && isShippingAlert ? (
                      <>
                        <span className="error-icon">
                          <img src={errorMsg?.src} alt={alert.mssg} />
                        </span>
                        <span>{i18n.bundleRestrictionMessage}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                {isModel ? (
                  <div className="col-12 col-sm-6 col-md-5">
                    <div className="row px-0 mx-0">
                      <div className="col-6 d-none d-sm-block px-0 text-center cart-items__td">
                        {quantity}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              {isEnableTabView && isReturnItem && !isComboSku ? (
                <p
                  className={`cart-item__return_description ${
                    isReturnCancelledOrRejected ? "text-error" : ""
                  }`}
                >
                  {htmlParser(
                    getReturnStatusDescription(
                      status,
                      i18n,
                      returnCancelledMessage,
                      returnRejectedMessage
                    )
                  )}
                </p>
              ) : null}
            </section>
          </div>
        </div>
        {!checkoutMode ? (
          <div
            className={`col-12 col-sm-6 ${
              orderMode ? "col-md-4" : isAlert ? "col-md-4" : "col-md-6"
            } cart-item__info print-right`}
          >
            <div className="row cart-item__qty-price cart-qty-price-mobile mx-0 px-0">
              {isEnableTabView && !isComboSku ? (
                <div className="cart-item-spacer-non-comboskus" />
              ) : null}
              <div
                className={`cart-item__qty-mobile ${
                  isEnableTabView ? "compact" : ""
                }`}
              >
                &nbsp;
              </div>
              <div
                className={`${
                  isDeliveryOption || isAlert
                    ? "col-6"
                    : orderMode
                    ? "col-6"
                    : "col-4"
                } cart-item__qty ${isEnableTabView ? "px-1" : "px-0"}`}
              >
                {!checkoutMode && !orderMode ? (
                  <QuantitySelector
                    size="small"
                    value={prodQuantity}
                    onChange={handleChange}
                    loading={qtyLoading || loading}
                    productData={data}
                    source="cart-landing"
                    isBundleParent={isBundleParent}
                    productInfoData={JSON.parse(cartItemProductInfo)}
                  />
                ) : (
                  <>
                    {shouldRenderQuantity ? (
                      <span
                        className="cart-item__label"
                        aria-description={i18n.quantity}
                      >
                        {isDeliveryOption || isAlert || orderMode ? (
                          <span
                            className={`${
                              isAlert ? "hide-desktop-qty" : ""
                            } --desktop`}
                          >
                            {i18n.qty} :{" "}
                          </span>
                        ) : null}
                        {data.quantity}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
              {
                <>
                  {!isAlert && !isDeliveryOption && !orderMode ? (
                    <div
                      className={`${
                        orderMode ? "col-6" : "col-6"
                      }  cart-item__discount-container discount-container-padding`}
                    >
                      {!is3COrder && (
                        <>
                          <div
                            className="cart-item__price"
                            aria-live="assertive"
                            aria-atomic
                          >
                            <NumericFormat
                              className="cart-item__total-price hide-mobile-price"
                              aria-description={i18n.unitPrice}
                              value={getDiscountPrice(
                                data?.discountPrice,
                                data?.discountUnitPrice,
                                data?.unitPrice
                              )}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={currencySign}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                            {data?.brand?.toLowerCase() === "annsacks" && (
                              <span className="hide-mobile-price">
                                {getTmpType({ category, i18n })}
                              </span>
                            )}
                            {(isDeliveryOption ? !comboChild : true) && (
                              <div
                                className={`${
                                  isDeliveryOption ? "col-6" : "col-2"
                                } text-right cart-item__total px-0 mobile-total-price`}
                                aria-live="assertive"
                                aria-atomic
                              >
                                <NumericFormat
                                  className="cart-item__total-price "
                                  aria-label={`${i18n.total} ${i18n.itemPrice}`}
                                  value={data.totalPrice}
                                  displayType={"text"}
                                  thousandSeparator={true}
                                  prefix={currencySign}
                                  decimalScale={2}
                                  fixedDecimalScale={true}
                                />{" "}
                              </div>
                            )}
                            <br className="show-mobile" />
                            {data.discountPrice > 0 && (
                              <NumericFormat
                                className="cart-item__span  car-total-qty-desktop"
                                value={
                                  Number(data?.discountUnitPrice)
                                    ? data?.discountUnitPrice
                                    : unitPrice
                                }
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={currencySign}
                                decimalScale={2}
                              />
                            )}
                            {data?.brand?.toLowerCase() === "annsacks" &&
                              data?.discountPrice > 0 && (
                                <span className="cart-item__span  hide-mobile-price">
                                  {getTmpType()}
                                </span>
                              )}
                            {data.discountPrice > 0 && (
                              <NumericFormat
                                className="cart-item__span  car-total-qty-mobile"
                                value={data.unitPrice * quantity}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={currencySign}
                                decimalScale={2}
                              />
                            )}
                            {/* {!isBundleParent ? (
                              <p className="cart-bundle-discount-message">
                                {i18n.saveOnBundle("12")}
                              </p>
                            ) : null} */}
                          </div>
                          {!orderMode && data.discountPrice > 0 && (
                            <div
                              className={
                                orderMode
                                  ? "cart-item__display-discount-order"
                                  : !checkoutMode
                                  ? "cart-item__display-discount-cart"
                                  : "cart-item__display-discount"
                              }
                              aria-label={`Striked price`}
                              aria-required="true"
                              aria-invalid="false"
                            >
                              <div
                                className={
                                  orderMode
                                    ? "cart-item__discount-price --offPercentage"
                                    : !checkoutMode
                                    ? "cart-item__discount-price --offPercentCart"
                                    : "cart-item__discount-price --offPercent"
                                }
                              >
                                <span>
                                  {data.discountPercent
                                    ? `${data.discountPercent}${i18n.discountPercentage}`
                                    : data.discountAmount
                                    ? `${currencySign}${discountVal} ${i18n.off}`
                                    : ""}
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : null}
                  {/* Always render the combo-child's price when tabs are enabled. and Always Hide combo-parent price when tabs are enabled */}
                  {shouldRenderPrice ? (
                    <div
                      className={`${
                        isDeliveryOption || isAlert || orderMode
                          ? "col-6"
                          : "col-2"
                      } text-right cart-item__total px-0 hide-price-popup-mobile`}
                      aria-live="assertive"
                      aria-atomic
                    >
                      <NumericFormat
                        className="cart-item__total-price  hide-mobile-price"
                        aria-label={`${i18n.total} ${i18n.itemPrice}`}
                        value={data.totalPrice}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={currencySign}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />

                      {(isDeliveryOption && data.discountPrice > 0) ||
                        (orderMode && (
                          <div className="cart-item__display-discount">
                            <div className="cart-item__discount-price">
                              <NumericFormat
                                value={data.unitPrice * data.quantity}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={currencySign}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : null}
                </>
              }
              <div className="clear"></div>
            </div>

            {shouldRenderTrackingButtons(
              data.trackingLinks,
              data.svgId,
              isEnableTabView
            ) ? (
              <div
                className={
                  isEnableTabView
                    ? "tracking-links-wrapper"
                    : "cart-item__links"
                }
              >
                {data.trackingLinks?.map((link, i) => {
                  return (
                    <React.Fragment key={link.trackingId}>
                      {data.availableQty || isEnableTabView ? (
                        <div
                          key={i}
                          className={`cart-item__link cart-item__track-btn ${
                            isEnableTabView
                              ? "cart-item__link-tabs-enabled "
                              : ""
                          }`}
                        >
                          <a
                            href={link.trackingURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={
                              trackingLinkAnalyticsData ? "gbh-data-layer" : ""
                            }
                            data-gbh-data-layer={
                              trackingLinkAnalyticsData
                                ? getTrackingLinkAnalyticsData(
                                    link.trackingURL,
                                    trackingLinkAnalyticsData,
                                    isEnableTabView,
                                    activeTab
                                  )
                                : null
                            }
                            data-gbh-data-layer-custom={cartItemProductInfo}
                          >
                            {i18n.tracking}
                          </a>

                          {isEnableTabView && link.shipmentDate ? (
                            <p className="cart-item__delivery-date">
                              {i18n.shipmentDate}{" "}
                              {formatDate(
                                parseDate(link.shipmentDate),
                                "MM/dd/yy"
                              )}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </React.Fragment>
                  )
                })}
                {data.svgId && !orderMode && !isAsProduct && (
                  <div className="cart-item__link">
                    <span
                      className={linkAnalyticsData ? "gbh-data-layer" : ""}
                      role="link"
                      tabIndex="0"
                      data-gbh-data-layer={linkAnalyticsData || null}
                      data-gbh-data-layer-custom={cartItemProductInfo}
                      onClick={() => setPartsModal(true)}
                    >
                      {i18n.viewPartsDiagram}
                    </span>
                  </div>
                )}
              </div>
            ) : null}
            {showRadio && (
              <div className="cart-item__radio-group">
                <Radio
                  value="remove"
                  checked={action === "remove"}
                  type="black"
                  label={i18n.remove}
                  name={`kf-shipping-alert-${data.sku}`}
                  onSelect={e => setAction(e.target.value)}
                />
              </div>
            )}
          </div>
        ) : null}

        {isMsgStatus && !orderMode ? (
          <div
            className={`${
              orderMode && email ? "order-mode-hide-main-section" : "col-12"
            }`}
          >
            <div className="cart-item__cta-wrapper hide-mobile-checkout-section">
              <div className="cart-blank-section">&nbsp;</div>
              {!checkoutMode && (
                <div className="remove-sec-cart">
                  <div
                    className={`cart-item__cta ${loading ? "--loading" : ""}`}
                  >
                    <span
                      tabIndex="0"
                      role="button"
                      aria-label={`${data.name} ${i18n.remove}`}
                      onClick={handleRemoveItem}
                    >
                      Remove
                    </span>
                  </div>
                </div>
              )}
              <div className="clear"></div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  )
}

export default CartItemTile
